import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FilePlus2, Loader2, Receipt } from "lucide-react";

import { useCancelClaim, useMyClaims } from "../hooks";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import type { ClaimStatus, ClaimType } from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) { return `KES ${n.toLocaleString()}`; }

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

function typeLabel(t: ClaimType) {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const PAGE_SIZE = 15;

const STATUS_TABS: { value: ClaimStatus | "ALL"; label: string }[] = [
  { value: "ALL",              label: "All" },
  { value: "DRAFT",            label: "Draft" },
  { value: "PENDING_APPROVAL", label: "Pending" },
  { value: "APPROVED",         label: "Approved" },
  { value: "REJECTED",         label: "Rejected" },
  { value: "PAID",             label: "Paid" },
];

// ─── page ─────────────────────────────────────────────────────────────────────

export default function MyClaimsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState<ClaimStatus | "ALL">("ALL");

  const filters = {
    page,
    size: PAGE_SIZE,
    ...(activeTab !== "ALL" && { status: activeTab }),
  };

  const { data, isLoading, error } = useMyClaims(filters);
  const cancelMutation = useCancelClaim();
  const confirm = useConfirm();


  const claims     = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const total      = data?.totalElements ?? 0;

  function handleTabChange(tab: ClaimStatus | "ALL") {
    setActiveTab(tab);
    setPage(0);
  }

  async function handleCancel(id: number) {
    if (!(await confirm("Cancel this claim? This cannot be undone."))) return;
    await cancelMutation.mutateAsync({ id, comments: "Cancelled by employee" });
  }

  return (
    <div className="flex flex-col gap-4">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">My Claims</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/claims" className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
            ← Dashboard
          </Link>
          <Link
            to="/claims/new"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
          >
            <FilePlus2 className="h-4 w-4" /> New Claim
          </Link>
        </div>
      </div>

      {/* ── Status tabs ── */}
      <div className="flex gap-1 overflow-x-auto rounded-2xl border bg-white p-1.5 shadow-sm">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => handleTabChange(tab.value)}
            className={`shrink-0 rounded-xl px-4 py-1.5 text-xs font-bold transition-colors ${
              activeTab === tab.value
                ? "bg-emerald-700 text-white"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Table ── */}
      {error ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
          {(error as { response?: { status?: number } })?.response?.status === 404
            ? "No employee profile found for your account. Contact HR to set one up."
            : "Failed to load claims. If this persists, contact your system administrator."}
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-8 text-slate-400 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading claims…
        </div>
      ) : claims.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border bg-white py-12 shadow-sm">
          <Receipt className="h-10 w-10 text-slate-200" />
          <p className="text-sm text-slate-400">
            {activeTab === "ALL" ? "No claims submitted yet." : `No ${activeTab.replace(/_/g, " ").toLowerCase()} claims.`}
          </p>
          <Link
            to="/claims/new"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-800"
          >
            <FilePlus2 className="h-3.5 w-3.5" /> New Claim
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-400">
                <th className="px-5 py-3">Claim</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Amount</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Submitted</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {claims.map((c) => (
                <tr key={c.id} className="transition-colors hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-xs font-black text-emerald-700">
                        {c.claimType.charAt(0)}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-400">{c.claimNo}</div>
                        {c.currentStepName && (
                          <div className="text-[10px] text-slate-400">Step: {c.currentStepName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-semibold text-slate-700">{typeLabel(c.claimType)}</td>
                  <td className="px-3 py-3 font-black text-slate-800">{fmt(c.totalAmount ?? 0)}</td>
                  <td className="px-3 py-3"><ClaimStatusBadge status={c.status} /></td>
                  <td className="px-3 py-3 text-xs text-slate-400">{fmtDate(c.submittedAt)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/claims/${c.id}`)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-100"
                      >
                        View
                      </button>
                      {c.status === "DRAFT" && (
                        <button
                          onClick={() => navigate(`/claims/${c.id}/edit`)}
                          className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-100"
                        >
                          Edit
                        </button>
                      )}
                      {(c.status === "DRAFT" || c.status === "PENDING_APPROVAL") && (
                        <button
                          onClick={() => handleCancel(c.id)}
                          disabled={cancelMutation.isPending}
                          className="rounded-lg border border-red-100 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-40"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t bg-slate-50 px-5 py-3">
            <span className="text-xs text-slate-400">{total} claim{total !== 1 ? "s" : ""}</span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="text-xs font-bold text-slate-500">
                {page + 1} / {totalPages || 1}
              </span>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
